import './FeatureFlagCard.scss';

import { createAsyncSelectors } from '@lib/selectors';
import {
	FeatureFlagLabel
} from '@sonar-software/sonar-core';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import { ShardCatalogSearch } from '../../ShardCatalogSearch';
import { SonarCard } from '@ui/general';
import { SonarTable } from '@ui/SonarTable';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { ADMIN_DASHBOARD_COPY, COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import {
	adminActions,
	featureFlagCustomer,
	featureFlagMetadata,
	ShardSearchEntryDetails
} from '@sonar-software/sonar-admin';
import { Button, Checkbox, Flex, Popover, Radio, Select, Space, Tag } from 'antd';
import { CloseOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import React, { useCallback, useMemo } from 'react';
import { useAction, useAsyncProcessing, useBoolean } from '@hooks';

const overrideDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.override);
const setDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.useDefault);
const patchSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.patch);

export interface FeatureFlagCardProps {
	featureFlag: featureFlagMetadata | null;
	groupOptions: Array<{ label: string; value: string | number }>;
}

const lowercaseFeatureFlags = objectKeys(FeatureFlagLabel).reduce((obj, key) => {
	obj[ key.toLocaleLowerCase() ] = FeatureFlagLabel[ key ];

	return obj;
}, {} as Record<string, string>);

function getRowKey(entry: featureFlagCustomer, flagName: string) {
	return `${flagName}-${entry.id}-${entry.shardName}`;
}

const stateOptions = [
	{
		label: COPY_GENERIC.FORCE_ENABLED,
		value: true
	},
	{
		label: COPY_GENERIC.UNSET,
		value: null
	},
	{
		label: COPY_GENERIC.FORCE_DISABLED,
		value: false
	}
];

export const FeatureFlagCard: React.FC<FeatureFlagCardProps> = ({ featureFlag, groupOptions }) => {
	const [ isOpen, setIsOpen ] = useBoolean(false);

	const resetShardCatalogDetails = useAction(adminActions.resetGetAdminSearchShardCatalogDetails);
	const overrideDefault = useAction(adminActions.requestPostOverrideAdminFeatureFlagByCustomerId);
	const resetGet = useAction(adminActions.requestGetAdminFeatureFlags);
	const patch = useAction(adminActions.requestPatchAdminFeatureFlagById);
	const setDefault = useAction(adminActions.requestPostUseDefaultAdminFeatureFlagByCustomerId);

	const exceptionCount = featureFlag?.exceptionCustomers
		.filter((customer) => customer.enabled !== featureFlag?.enabledByDefault).length ??
		0;

	const overrideProcess = useAsyncProcessing({
		resetAction: resetGet,
		stateSelector: overrideDefaultSelectors.state
	});

	const setDefaultProcess = useAsyncProcessing({
		resetAction: resetGet,
		stateSelector: setDefaultSelectors.state
	});

	const patchProcess = useAsyncProcessing({
		resetAction: resetGet,
		stateSelector: patchSelectors.state
	});

	const handleAddCustomer = useCallback((details: ShardSearchEntryDetails | null | undefined) => {
		if (!details) {
			return;
		}

		setIsOpen.off();

		if (featureFlag &&
			!featureFlag.exceptionCustomers.find((customer) =>
				customer.id === details.customerId &&
                customer.shardName === details.shardName &&
                featureFlag.enabledByDefault !== customer.enabled)) {
			overrideProcess.watchNextCall();

			overrideDefault({
				customerId: details.customerId,
				customerName: details.customerName,
				enabled: !featureFlag.enabledByDefault,
				featureFlag: featureFlag.name,
				shardName: details.shardName
			});
		}

		resetShardCatalogDetails();
	}, [ featureFlag, setIsOpen, overrideDefault, overrideProcess, resetShardCatalogDetails ]);

	const handleSetDefault = useCallback((customer: featureFlagCustomer) => {
		if (featureFlag) {
			setDefaultProcess.watchNextCall();

			setDefault({
				customerId: customer.id,
				featureFlag: featureFlag.name,
				shardName: customer.shardName
			});
		}
	}, [ featureFlag, setDefault, setDefaultProcess ]);

	const handlePatch = useCallback((
		id : string,
		forcedValue?: boolean | null,
		groupId?: number | null,
		groupName?: string | null,
		defaultValue?: boolean
	) => {
		patchProcess.watchNextCall();

		patch({
			defaultValue,
			forcedValue,
			groupId,
			groupName,
			id
		});
	}, [ patch, patchProcess ]);

	const customerTableColumns = useMemo(() => [
		{
			dataIndex: 'name',
			key: 'name',
			render: (value: unknown, record: featureFlagCustomer) =>
				<Flex
					align='center'
					justify='space-between'>
					<SonarText>{ record.name }</SonarText>
					<Button
						onClick={ () => handleSetDefault(record) }
						type='text'><CloseOutlined />
					</Button>
				</Flex>,
			title: COPY_SPECIFIC.NAME
		}
	], [ handleSetDefault ]);

	if (!featureFlag) {
		return null;
	}

	return (
		<SonarCard
			className='feature-flag-card'
			extra={
				<Radio.Group
					buttonStyle='solid'
					id='feature-flag-state'
					onChange={ (e) => handlePatch(
						featureFlag.name,
						e.target.value
					) }
					options={ stateOptions }
					optionType='button'
					value={ featureFlag.state } />
			}
			title={
				<Space>
					{ lowercaseFeatureFlags[ featureFlag.name.toLowerCase() ] ?? featureFlag.name }
					<When condition={ featureFlag.enabledByDefault }>
						<Tag color={ 'geekblue' }>
							{ COPY_SPECIFIC.ENABLE_BY_DEFAULT }
						</Tag>
					</When>
				</Space>
			}>
			<Space
				direction='vertical'
				style={ { width: '100%' } }>
				<When condition={ featureFlag.state === null }>
					<Flex justify='space-between'>
						<Space>
							<UserOutlined />
							<SonarText weight='semibold'>
								{
									featureFlag.enabledByDefault ?
										COPY_SPECIFIC.DISABLED_CUSTOMERS_COUNT(exceptionCount) :
										COPY_SPECIFIC.ENABLED_CUSTOMERS_COUNT(exceptionCount)
								}
							</SonarText>
						</Space>
						<Popover
							content={
								<ShardCatalogSearch
									filtertype='customer'
									id='feature-flag-customer-search'
									onDetailsChanged={ handleAddCustomer }
									placeholder={ ADMIN_DASHBOARD_COPY.SEARCH_CUSTOMERS_PLACEHOLDER } />
							}
							destroyTooltipOnHide
							onOpenChange={ (value) => (value ? setIsOpen.on() : setIsOpen.off()) }
							open={ isOpen }
							placement='bottomRight'
							trigger='click'>
							<Button
								id='feature-flag-add-customer-button'
								type='primary'>
								<PlusOutlined />
								{ COPY_SPECIFIC.ADD_CUSTOMER }
							</Button>
						</Popover>
					</Flex>
					<SonarTable
						columns={ customerTableColumns }
						dataSource={
							featureFlag.exceptionCustomers
								.filter((customer) => customer.enabled !== featureFlag?.enabledByDefault)
						}
						id='exception-customers-table'
						pagination={ false }
						rowKey={ (e) => getRowKey(e, featureFlag.name) }
						scroll={ { x: 'max-content' } } />
				</When>
				<SonarText weight='semibold'>{ COPY_SPECIFIC.CONFIG }</SonarText>
				<SonarText>{ COPY_SPECIFIC.FEATURE_FLAG_GROUP }</SonarText>
				<Select
					allowClear
					className='feature-flag-group-select'
					id='feature-flag-group-select'
					onChange={ (groupId) => handlePatch(
						featureFlag.name,
						undefined,
						groupId,
						groupOptions?.find((x) => x.value === groupId)?.label ?? null
					) }
					onClear={ () => handlePatch(
						featureFlag.name,
						undefined,
						null,
						null
					) }
					options={ groupOptions }
					placeholder={ COPY_SPECIFIC.SELECT_GROUP }
					value={ featureFlag.groupId ?? undefined } />
				<Checkbox
					checked={ featureFlag.enabledByDefault }
					className='enable-by-default-checkbox'
					onChange={ (e) => handlePatch(
						featureFlag.name,
						undefined,
						undefined,
						undefined,
						e.target.checked
					) }>
					{ COPY_SPECIFIC.ENABLE_BY_DEFAULT }
				</Checkbox>
			</Space>
		</SonarCard>);
};
